import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

export interface UserSelection {
  id?: string;
  userId: number;
  organizationId: number;
  selectionType: string;
  selections: any; // JSON object containing the selections
  createdAt?: string;
  updatedAt?: string;
}

export interface SaveSelectionRequest {
  selectionType: string;
  selections: any;
}

// Submit Data specific selection types
export const SELECTION_TYPES = {
  SUBMIT_DATA_PREFERENCES: 'submit_data_preferences'
} as const;

// Interface for Submit Data preferences
export interface SubmitDataPreferences {
  selectedYear?: number;
  selectedMonth?: number;
  selectedIdentifierColumn?: string;
  selectedDataColumn?: string;
  selectedToggle?: 'whole' | 'percent';
}

class UserSelectionService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  }

  // Get user selection by type
  async getUserSelection(selectionType: string): Promise<UserSelection | null> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/user-selections/${encodeURIComponent(selectionType)}`,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user selection');
      }
    } catch (error: any) {
      if (error.response?.status === 404 || error.response?.data?.message?.includes('not found')) {
        return null; // No selection found, which is valid
      }
      console.error('Error fetching user selection:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user selection');
    }
  }

  // Save or update user selection
  async saveUserSelection(request: SaveSelectionRequest): Promise<UserSelection> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/user-selections`,
        request,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to save user selection');
      }
    } catch (error: any) {
      console.error('Error saving user selection:', error);
      throw new Error(error.response?.data?.message || 'Failed to save user selection');
    }
  }

  // Delete user selection
  async deleteUserSelection(selectionType: string): Promise<boolean> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/user-selections/${encodeURIComponent(selectionType)}`,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return true;
      } else {
        throw new Error(response.data.message || 'Failed to delete user selection');
      }
    } catch (error: any) {
      console.error('Error deleting user selection:', error);
      throw new Error(error.response?.data?.message || 'Failed to delete user selection');
    }
  }

  // Get all user selections
  async getAllUserSelections(): Promise<UserSelection[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/user-selections`,
        this.getAuthHeaders()
      );
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user selections');
      }
    } catch (error: any) {
      console.error('Error fetching user selections:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch user selections');
    }
  }

  // Specific method for Submit Data preferences
  async getSubmitDataPreferences(): Promise<SubmitDataPreferences | null> {
    try {
      const selection = await this.getUserSelection(SELECTION_TYPES.SUBMIT_DATA_PREFERENCES);
      return selection?.selections || null;
    } catch (error) {
      console.error('Error getting Submit Data preferences:', error);
      return null;
    }
  }

  // Specific method to save Submit Data preferences
  async saveSubmitDataPreferences(preferences: SubmitDataPreferences): Promise<UserSelection> {
    return this.saveUserSelection({
      selectionType: SELECTION_TYPES.SUBMIT_DATA_PREFERENCES,
      selections: preferences
    });
  }
}

export const userSelectionService = new UserSelectionService();
export default userSelectionService;